import { useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import type { CommonListItem } from 'components/ui/lists/CommonList';

import TertiaryText from 'components/core/typography/TertiaryText';
import TextRow from 'components/core/typography/TextRow';
import FilterableListDialog from 'components/ui/dialogs/FilterableListDialog';
import { userCondensedListQuery } from 'containers/users/UserCondensedListQuery';
import { ElementTestId } from 'enums/testing';
import { translate } from 'utils/intlUtils';

import type { UserCondensedListQuery } from 'store/client/graphql';

import { FONT_SIZE_13 } from 'styles/tokens';

interface Props {
  /**
   * The ID of the current user
   */
  myId: string;
  /**
   * What to do when closing the dialog
   */
  onCancel: () => void;
  /**
   * The function to call when a user is selected from the dialog
   */
  onUserSelected: (user: UserCondensedListQuery['connection']['edges'][number]['node']) => void;
}

const RooftopLabel = styled(TertiaryText)`
  font-size: ${FONT_SIZE_13};
`;

const UserSelector = ({ myId, onCancel, onUserSelected }: Props) => {
  const [listFilterKeyword, setListFilterKeyword] = useState<string>();

  const { data: users } = useQuery(userCondensedListQuery, {
    variables: { filter: { active: true }, first: 100, keyword: listFilterKeyword },
  });

  const options: CommonListItem[] = useMemo(() => {
    const edges = users?.connection?.edges;
    if (!edges) {
      return [];
    }
    const allUsers = edges.map(edge => edge.node);
    const filteredUsers = allUsers.filter(user => user.id !== myId);

    return filteredUsers.map(user => ({
      id: user.id,
      label: user.displayName,
      labelElement: (
        <div>
          <TextRow>{user.displayName}</TextRow>
          {user.rooftops && user.rooftops.length > 0 ? (
            <TextRow>
              <RooftopLabel>{user.rooftops[0].name.value}</RooftopLabel>
            </TextRow>
          ) : null}
        </div>
      ),
      onClick: () => {
        onUserSelected(user);
        onCancel();
      },
    }));
  }, [users?.connection?.edges, myId, onUserSelected, onCancel]);

  return (
    <FilterableListDialog
      headerTitle={translate.t('login_as')}
      isFixedToTop
      isOpen
      isUsingServerSideSearch
      listItems={options}
      onCancel={() => {
        onCancel();
        setListFilterKeyword('');
      }}
      onSearchTermChanged={keyword => setListFilterKeyword(keyword)}
      searchPlaceholderText={translate.t('search_dealers')}
      testId={ElementTestId.LOGIN_AS_MODAL}
      width={350}
    />
  );
};

export default UserSelector;
